import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import FormDialog from "../common/FormDialog";
import FormField from "../common/FormField";
import useFormValidation from "../../hooks/useFormValidation";
import {
  email,
  mobile,
  normalizeTenantNameInput,
  required,
  acceptNationalMobileInput,
  sanitizeNationalPhone,
  tenantName,
  toApiMobileNumber,
} from "../../utils/formValidation";
import { tenantmgmtService } from "../../services";
import { appActions } from "../../redux/actions";
import { getApiErrorMessage } from "../../helpers";

const emptyForm = {
  name: "",
  email: "",
  contactPerson: "",
  mobile: "",
};

const tenantSchema = {
  name: [required("Tenant name is required."), tenantName()],
  email: [required("Email is required."), email()],
  contactPerson: [required("Contact person is required.")],
  mobile: [mobile()],
};

const getTenantId = (tenant) => tenant?.id || tenant?._id || tenant?.originalId || "";

const CreateTenant = ({
  open,
  onClose,
  tenantData,
  onSuccess,
  existingTenants = [],
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [apiError, setApiError] = React.useState("");
  const isEdit = Boolean(tenantData);

  const existingNameSet = useMemo(() => {
    const set = new Set();
    (existingTenants || []).forEach((tenant) => {
      const name = String(tenant?.name || tenant?.slug || "")
        .trim()
        .toLowerCase();
      if (name) set.add(name);
    });
    return set;
  }, [existingTenants]);

  const initialValues = useMemo(() => {
    if (!tenantData) return emptyForm;
    return {
      name: tenantData.name || "",
      email: tenantData.email || "",
      contactPerson: tenantData.contactPerson || "",
      mobile: sanitizeNationalPhone(tenantData.mobile || ""),
    };
  }, [tenantData]);

  const { values, getFieldProps, validateAll, reset, setFieldValue } =
    useFormValidation(initialValues, tenantSchema);

  useEffect(() => {
    if (!open) return;
    setApiError("");
    setLoading(false);
    reset(initialValues);
  }, [open, initialValues, reset]);

  const handleSubmit = async () => {
    const { isValid, errors } = validateAll();
    if (!isValid) {
      setApiError(
        errors.name ||
          errors.email ||
          errors.contactPerson ||
          errors.mobile ||
          "Please fill in all required fields correctly."
      );
      return;
    }

    const nextName = values.name.trim();
    const currentId = isEdit ? getTenantId(tenantData) : "";

    if (!isEdit) {
      const nameTaken = existingNameSet.has(nextName.toLowerCase());
      if (nameTaken) {
        setApiError(
          `Tenant "${nextName}" already exists. Choose a different name (e.g. jioTenant2).`
        );
        return;
      }
    }

    const payload = isEdit
      ? {
          email: values.email.trim(),
          contactPerson: values.contactPerson.trim(),
        }
      : {
          name: nextName,
          email: values.email.trim(),
          contactPerson: values.contactPerson.trim(),
        };
    if (isEdit) {
      const mobileValue = sanitizeNationalPhone(values.mobile);
      if (mobileValue) {
        // IAM requires E.164 (+91…); 10-digit local format is rejected.
        payload.mobile = toApiMobileNumber(mobileValue);
      }
    }

    setApiError("");
    setLoading(true);
    try {
      if (isEdit) {
        if (!currentId) {
          setApiError("Tenant ID is missing.");
          return;
        }
        await tenantmgmtService.updateTenant(currentId, payload);
        dispatch(appActions.apiSuccessMessage("Tenant updated successfully."));
      } else {
        await tenantmgmtService.createTenant(payload);
        dispatch(appActions.apiSuccessMessage("Tenant created successfully."));
      }
      onSuccess?.();
      onClose?.();
    } catch (err) {
      const message = getApiErrorMessage(err) || "";
      // IAM often inserts the tenant then fails later (DNS/provision) and
      // returns 4xx/5xx — so the row still appears in the list.
      const likelyCreated =
        !isEdit &&
        /provision|privison|something went wrong while creating system tenant/i.test(
          message
        );
      if (likelyCreated) {
        onSuccess?.();
        setApiError(
          `Tenant may have been created, but provisioning failed (${message}). Check the tenant list before retrying.`
        );
        dispatch(
          appActions.apiErrorMessage(
            `Provisioning failed for "${nextName}" — check if it already appears in the list.`
          )
        );
      } else {
        setApiError(
          message ||
            (isEdit ? "Failed to update tenant." : "Failed to add tenant.")
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title={isEdit ? "Edit Tenant" : "Add Tenant"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      error={apiError}
      onClearError={() => setApiError("")}
      submitLabel={isEdit ? "Update Tenant" : "Add Tenant"}
      loadingLabel={isEdit ? "Updating..." : "Creating..."}
    >
      <FormField
        label="Tenant Name"
        required
        {...getFieldProps("name")}
        disabled={isEdit}
        onChange={(event) =>
          setFieldValue("name", normalizeTenantNameInput(event.target.value))
        }
        helperText={
          getFieldProps("name").helperText ||
          (isEdit
            ? "Tenant name cannot be changed."
            : "Letters and numbers only (camelCase OK). No spaces, underscore, or hyphen. Example: PanasonicTesting")
        }
      />
      <FormField label="Email" type="email" required {...getFieldProps("email")} />
      <FormField label="Contact Person" required {...getFieldProps("contactPerson")} />
      {isEdit ? (
        <FormField
          label="Mobile"
          {...getFieldProps("mobile")}
          onChange={(event) => {
            const accepted = acceptNationalMobileInput(event.target.value);
            if (accepted === null) return;
            setFieldValue("mobile", accepted);
          }}
          inputProps={{ maxLength: 10, inputMode: "numeric" }}
        />
      ) : null}
    </FormDialog>
  );
};

export default CreateTenant;
